<template>
  <v-sheet class="pa-5">
    <div>
      <div class="d-flex justify-space-between mx-2">
        <div class="mt-1 text-h6">{{ googleAccountOwner }}</div>
        <div>
          <v-btn class="mr-2 mr-lg-5" @click="showSwitchEvent">
            {{ event ? "Switch Event" : "Choose Existing Event" }}
          </v-btn>
        </div>
      </div>
      <hr class="my-5" />
      <div v-if="!loadingEvent">
        <google-calendar-event
          @deletedEvent="deletedGoogleEvent"
          v-if="event && event.id"
          v-model="event"
          :owner="googleAccountOwner"
          :key="event.id"
        />
        <div v-else>
          <v-alert
            colored-border
            color="secondary"
            border="left"
            class="ma-5"
            type="info"
          >
            No Google Calendar event is associated with this schedule.
          </v-alert>
          <v-card>
            <v-card-title>
              Create New Google Calendar Event
            </v-card-title>
            <v-card-subtitle>
              Schedule - {{ courseSchedule | scheduleText }}
            </v-card-subtitle>
            <v-card-text>
            <v-form ref="step2-form" lazy-validation>
              <v-row>
                <v-col cols="12" lg="12">
                  <v-text-field v-model="eventTitle" label="Event Title"/>
                </v-col>
                <v-col cols="12" lg="12">
                  <v-textarea rows="1" :auto-grow="true" v-model="eventDescription" label="Event Description"/>
                </v-col>
                <v-col cols="12" lg="12">
                  <v-select
                    v-model="googleCalendarOwner"
                    width="200px"
                    :items="googleAccounts"
                    item-text="name"
                    item-value="value"
                    label="Google Calendar Owner"
                  ></v-select>
                </v-col>
                <v-col cols="12" lg="12">
                  <v-select
                    width="200px"
                    v-model="selectedReminders"
                    multiple
                    :items="reminders"
                    label="Scheduled Reminder"
                  ></v-select>
                </v-col>
              </v-row>
            </v-form>
            </v-card-text>
            <v-card-actions>
              <v-spacer/>
              <v-btn :disabled="isAppLoading" @click="saveNewCalendarEvent">
                Save
              </v-btn>
              <v-spacer/>
            </v-card-actions>
          </v-card>
        </div>
      </div>
      <div v-else>
        <v-skeleton-loader type="card-heading, image" />
      </div>
    </div>

    <v-dialog max-width="90%" v-model="switchEventDialog">
      <v-card min-height="600">
        <v-card-title>
          Switch Google Calendar Event
          <v-btn icon class="ml-auto" @click="switchEventDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-select
            :loading="isAppLoading"
            v-model="newOwner"
            :items="availableOwners"
            item-text="name"
            item-value="email"
            label="Choose a Google Calendar Owner"
            return-object
            @change="loadOwnerGoogleEvents"
          />

          <div v-if="newOwnerEvents && newOwnerEvents.length > 0">
            <div class="text-subtitle-2 mb-2 mb-lg-5">
              Pick an existing Google calendar event below
            </div>

            <div v-if="newOwnerEvents && newOwnerEvents.length > 10">
              <v-text-field v-model="eventFilter" label="Filter Events" />
            </div>
            <v-expansion-panels>
              <v-expansion-panel
                v-for="e in filteredNewOwnerEvents"
                :key="e.id"
              >
                <v-expansion-panel-header>
                  <div>
                      <span class="text-subtitle-2">{{ e.summary ? e.summary : "Untitled Event" }}
                        <v-icon small v-if="e.recurrence && e.recurrence.length > 0">mdi-calendar-sync-outline</v-icon>
                      </span>
                      <span
                        class="text-caption"
                        v-if="isRecurring(e)"
                      >
                      <br/>
                        <span v-html="rRuleToText(e)"></span>
                      </span>
                      <span v-else>
                        {{ formatDate(e.start, "date time") }} -
                        {{ formatDate(e.end, "time") }}
                      </span>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div class="d-flex justify-center mb-2">
                    <v-btn color="primary" @click="replaceEvent(e)">
                      Replace with this Event
                    </v-btn>
                  </div>
                  <hr />
                  <google-calendar-event
                    :allowDelete="false"
                    :allowSetReminders="false"
                    :allowAddAttendee="false"
                    :allowRemoveAttendee="false"
                    :value="e"
                    :key="e.id"
                  />
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
          <div v-else>
            <v-alert
              colored-border
              color="secondary"
              border="left"
              class="ma-5"
              type="info"
            >
              No google event found for this account</v-alert
            >
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-sheet>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
const GoogleCalendarEvent = () =>
  import("@/components/google/GoogleCalendarEvent.vue");
import { rrulestr } from "rrule";
import moment from "moment";

export default {
  props: {
    eventId: {
      type: String,
      required: false,
    },
    calendarId: {
      type: String,
      required: false,
    },
    accountOwner: {
      type: String,
      required: false,
    },
    courseSchedule: {
      type: Object,
      required: false,
    },
  },
  data: () => {
    return {
      event: null,
      switchEventDialog: false,
      availableOwners: [],
      newOwner: null,
      eventFilter: null,
      newOwnerEvents: [],
      filteredNewOwnerEvents: [],
      loadingEvent: false,
      googleAccounts: [],
      googleCalendarOwner: null,
      eventTitle: null,
      eventDescription: null,
      selectedReminders: [1440, 60],
      reminders: [{text: '1 Day', value: 1440}, {text: '5 hours', value: 300}, {text: '1 hour', value: 60}],
    };
  },
  watch: {
    eventFilter(value) {
      if (!value) {
        this.filteredNewOwnerEvents = this.newOwnerEvents;
      } else {
        const regex = new RegExp(value, "i");
        this.filteredNewOwnerEvents = this.newOwnerEvents.filter((e) =>
          regex.test(e.summary)
        );
      }
    },
  },
  mounted() {
    this.loadingEvent = true;

    this.loadGoogleAccounts();
    
    if (this.courseSchedule) {
        console.log('course schedule: ', this.courseSchedule)
        //Default title and description for new event
        this.eventTitle = this.courseSchedule.course.name;
        this.eventDescription = this.courseSchedule.course.description;
    }

    console.log("loading calendar...");
    this.loadEvent();
  },
  components: {
    GoogleCalendarEvent,
  },
  computed: {
    ...mapGetters(["isAppLoading", "currentUser"]),
    googleEventId:{
      get(){
        if(this.eventId){
          return this.eventId;
        }
        else if(this.courseSchedule.googleEventId){
          return this.courseSchedule.googleEventId;
        }
        
        return null;
      },
      set(eId){
        return eId;
      } 
    },
    googleCalendarId:{
      get(){
        if(this.calendarId){
          return this.calendarId;
        }
        else if(this.courseSchedule.googleCalendar){
          return this.courseSchedule.googleCalendar;
        }
        return null;
      },
      set(eId){
        return eId;
      } 
    },
    googleAccountOwner:{
      get(){
        if(this.accountOwner){
          return this.accountOwner;
        }
        else if(this.courseSchedule.googleCalendarAccount){
          return this.courseSchedule.googleCalendarAccount;
        }
        return null;
      },
      set(eId){
        return eId;
      } 
    },
  },
  methods: {
    ...mapActions(["addErrorMessage", "addSuccessMessage"]),
    loadEvent() {
      if (this.googleAccountOwner && this.googleEventId) {
        const params = {
          event_id: this.googleEventId,
          calendar_account: this.googleAccountOwner,
        };
        this.$axios
          .get("/google-calendar/get-event", { params })
          .then((resp) => {
            this.event = resp.data.data;
            this.loadingEvent = false;
          });
      } else {
        this.loadingEvent = false;
      }
    },
    showSwitchEvent() {
      this.loadOwners().then((owners) => {
        if (owners) {
          this.newOwner = { email: this.currentUser.email };
          this.loadOwnerGoogleEvents();
        }
      });
      this.switchEventDialog = true;
    },
    changeEvent() {
      console.log("changing google calendar event");
    },
    async loadOwners() {
      return this.$axios
        .get("/account/gsuite-accounts")
        .then((resp) => {
          const gSuiteAccounts = resp.data;
          this.availableOwners = gSuiteAccounts.map((acct) => {
            return {
              name: `${acct.email} (${acct.firstName} ${acct.lastName})`,
              email: acct.email,
            };
          });

          return this.availableOwners;
        })
        .catch((error) => {
          console.log(error);
        });
    },
    loadOwnerGoogleEvents() {
      this.eventFilter = null;
      const params = {
        calendar_account: this.newOwner.email,
      };
      this.$axios
        .get("/google-calendar/get-events", { params })
        .then((resp) => {
          const unfilteredEvents = resp.data.data;

          //Filter out all recurring events that has ended.
          if (unfilteredEvents) {
            console.log(`unfiltered list size: ${unfilteredEvents.length}`);
            this.newOwnerEvents = unfilteredEvents.filter((e) => {
              if (e.recurrence && e.recurrence.length > 0) {
                const rrule = e.recurrence[0];
                const rule = rrulestr(rrule);
                if (
                  rule.options.until &&
                  moment(rule.options.until).isBefore(moment())
                ) {
                  return false;
                } else {
                  return true;
                }
              } else {
                return true;
              }
            });

            this.filteredNewOwnerEvents = this.newOwnerEvents;

            console.log(`filtered list size: ${this.newOwnerEvents.length}`);
          }
        });
    },
    replaceEvent(event) {
      console.log("replace event for course schedule");
      const data = {
        calendarEventId: event.id,
        googleAccount: this.newOwner.email,
        courseScheduleId: this.courseSchedule.id,
      };
      this.$axios
        .post("/course-schedule/replace-calendar-event", data)
        .then((resp) => {
          const updatedSchedule = resp.data.data;
          this.addSuccessMessage("Replaced calender event");
          this.$emit("replacedEvent", updatedSchedule);
          this.switchEventDialog = false;
        });
    },
    saveNewCalendarEvent(){
      const data = {
          courseScheduleId: this.courseSchedule.id,
          googleAccount: this.googleCalendarOwner,
          reminders: this.selectedReminders,
          calendarEventTitle: this.eventTitle,
          calendarEventDescription: this.eventDescription,
          createGoogleCalendarEvent: true
      }
      this.$axios.post('/course-schedule/new-google-calendar-event', data)
        .then( resp => {
          this.event = resp.data.data;
          if(this.event && this.event.id){
            this.googleEventId = this.event.id;
          }
          else{
            this.addErrorMessage("Error: Google event not created");
          }
        })

    },
    deletedGoogleEvent(event) {
      console.log("deleted event....");
      const data = {
        courseScheduleId: this.courseSchedule.id,
        deleteOnGoogleCalendar: true
      };
      this.$axios
        .post("/course-schedule/clear-calendar-event", data)
        .then((resp) => {
          const updatedSchedule = resp.data.data;
          this.courseSchedule.googleEventId = null;
          this.courseSchedule.googleCalendar = null;
          this.courseSchedule.googleCalendarAccount = null;
          
          this.$emit("replacedEvent", updatedSchedule);
          this.$emit("deleted", event);
        });
    },
    loadGoogleAccounts() {
      this.$axios
        .get("/account/gsuite-accounts")
        .then((resp) => {
          const gSuiteAccounts = resp.data;
          this.googleAccounts = gSuiteAccounts.map((acct) => {
            return {
              name: `${acct.email} (${acct.firstName} ${acct.lastName})`,
              value: acct.email,
            };
          });
          if(this.googleAccounts && this.googleAccounts.length > 0 && !this.googleCalendarOwner){
            const currentUserEmail = this.currentUser.email;
            const matchingAccount = gSuiteAccounts.find(gsa => gsa.email == currentUserEmail);
            if(matchingAccount){
              this.googleCalendarOwner = matchingAccount.email;
            }
          }
        })
        .catch((error) => {
          console.log(error);
        });
    },
    rRuleToText(event) {
      if(!event.start.dateTime){
        return 'Unknown event time'
      }
      const startDate = this.formatDate(event.start, "date with dow");
      const startTime = this.formatDate(event.start, "time");
      const endTime = this.formatDate(event.end, "time");
      if (this.isRecurring(event)) {
        const recurrenceRrule = event.recurrence.find(r => r.startsWith('RRULE:'));
        const recurrenceExdate = event.recurrence.find(r => r.startsWith('EXDATE;'));
        const rrule = recurrenceRrule + (recurrenceExdate ? `\n${recurrenceExdate.replace(/EXDATE;.*:/, 'EXDATE:')}`:'');

        const ruleSet = rrulestr(rrule, {forceset: true});
        const ruleText = ruleSet.rrules().map(r => r.toText()).join('\n');
        const exDates = ruleSet.exdates()
        let excludes = '';
        if(exDates && exDates.length > 0){
          excludes = `excluding ${exDates.map(d => moment(d).format("MMM DD")).join(', ')}`;
        }

        return `Starts ${startDate} from ${startTime} - ${endTime},<br/> ${ruleText} <br/> ${excludes}`;
      } else {
        return `${startDate} ${startTime}-${endTime}`;
      }
    },
    isRecurring(event){
      if(event && event.recurrence && event.recurrence.length > 0){
        return true;
      }
      else{
        return false;
      }
    },
    formatDate(d, mode) {
      if(!d.dateTime){
        console.log(d);
        return '';
      }

      if (mode === "time") {
        return new moment.utc(d.dateTime.value).utcOffset(d.dateTime.timeZoneShift).format("h:mm a");
      } else if (mode === "date time") {
        return new moment.utc(d.dateTime.value).utcOffset(d.dateTime.timeZoneShift).format("MMM DD, YYYY h:mm a");
      } else if (mode === "date time with dow") {
        return new moment.utc(d.dateTime.value).utcOffset(d.dateTime.timeZoneShift).format("ddd MMM DD, YYYY h:mm a");
      } else if (mode === "date") {
        return new moment.utc(d.dateTime.value).utcOffset(d.dateTime.timeZoneShift).format("MMM DD, YYYY");
      } else if (mode === "date with dow") {
        return new moment.utc(d.dateTime.value).utcOffset(d.dateTime.timeZoneShift).format("ddd MMM DD, YYYY");
      } else if (mode === "dow") {
        return new moment.utc(d.dateTime.value).utcOffset(d.dateTime.timeZoneShift).format("dddd");
      }
    },
  },
};
</script>

<style>
</style>